//
//  qaProgramChecklistRows.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module flattens the nested trial checklist into collection grid rows.
//

import {
    QaProgramChecklistCompletionState,
    type TrialQaProgramChecklistItemCompletionStateChange,
    type TrialQaProgramChecklistItemNote,
    type TrialQaProgramChecklistView
} from "src/api/Tmd.dtos";

/** Describes one checklist task alongside the category it was grouped under. */
export interface QaProgramChecklistRow {
    itemId?: number;
    categoryId?: number;
    categoryName: string;
    categoryRank: number;
    categoryCompletedCount: number;
    categoryApplicableCount: number;
    task: string;
    additionalInfo: string;
    rank: number;
    completionState: QaProgramChecklistCompletionState;
    active: boolean;
    noteCount: number;
    activityCount: number;
    notes: TrialQaProgramChecklistItemNote[];
    completionStateChanges: TrialQaProgramChecklistItemCompletionStateChange[];
}

/** Identifies the accordion panel a row belongs to, falling back to its category name. */
export const qaProgramChecklistCategoryKey = (row: QaProgramChecklistRow) =>
    row.categoryId ? `category-${row.categoryId}` : `category-${row.categoryName}`;

/**
 * Locates a task and the category it sits under, for headings that name both.
 *
 * Postconditions: Returns undefined for a nullish id or a task the checklist does not hold, so
 * callers can render an empty heading rather than guess. Missing names become Uncategorised.
 */
export const findQaProgramChecklistItem = (
    checklist: TrialQaProgramChecklistView | undefined,
    itemId: number | undefined
) => {
    if (!itemId) {
        return undefined;
    }

    for (const category of checklist?.categories ?? []) {
        const item = (category.items ?? []).find(candidate => candidate.id === itemId);

        if (item) {
            return { item, categoryName: category.name || "Uncategorised" };
        }
    }

    return undefined;
};

/**
 * Flattens the nested checklist view into grid rows carrying their category context.
 *
 * Postconditions: Categories are emitted in rank order and items retain their rank order within a
 * category, so the workflow order from the source workbook survives the flattening. Nullish ranks
 * sort last. Missing names become Uncategorised; the supplied view is not modified.
 */
export const flattenQaProgramChecklist = (
    checklist?: TrialQaProgramChecklistView
): QaProgramChecklistRow[] =>
    [...(checklist?.categories ?? [])]
        .sort(
            (left, right) =>
                (left.rank ?? Number.MAX_SAFE_INTEGER) - (right.rank ?? Number.MAX_SAFE_INTEGER) ||
                (left.name ?? "").localeCompare(right.name ?? "")
        )
        .flatMap(category =>
            [...(category.items ?? [])]
                .sort(
                    (left, right) =>
                        (left.rank ?? Number.MAX_SAFE_INTEGER) -
                            (right.rank ?? Number.MAX_SAFE_INTEGER) ||
                        (left.task ?? "").localeCompare(right.task ?? "")
                )
                .map<QaProgramChecklistRow>(item => ({
                    itemId: item.id,
                    categoryId: category.id,
                    categoryName: category.name || "Uncategorised",
                    categoryRank: category.rank ?? Number.MAX_SAFE_INTEGER,
                    categoryCompletedCount: category.completedCount ?? 0,
                    categoryApplicableCount: category.applicableCount ?? 0,
                    task: item.task ?? "",
                    additionalInfo: item.additionalInfo ?? "",
                    rank: item.rank ?? Number.MAX_SAFE_INTEGER,
                    completionState: item.completionState ?? QaProgramChecklistCompletionState.No,
                    // A task in a deactivated category is not live either, so it hides with the
                    // rest of that category unless inactive rows are being shown.
                    active: item.active !== false && category.active !== false,
                    noteCount: item.noteCount ?? item.notes?.length ?? 0,
                    activityCount:
                        item.activityCount ??
                        (item.notes?.length ?? 0) + (item.completionStateChanges?.length ?? 0),
                    notes: item.notes ?? [],
                    completionStateChanges: item.completionStateChanges ?? []
                }))
        );
